/**
 * Thin client-side wrapper around the Meta Pixel loaded in `app/layout.tsx`.
 *
 * Purchase / InitiateCheckout are fired by Shopify's own Meta integration on its
 * hosted checkout — our headless storefront never renders Shopify's theme, so cart
 * adds are invisible to it and AddToCart has to be fired from here.
 *
 * Exception: the Apple Pay / Google Pay wallet flow (src/components/comix/wallet) never
 * reaches Shopify's checkout, so Shopify sends nothing for it — that flow fires its own
 * InitiateCheckout / AddPaymentInfo / Purchase here (with eventIDs). Purchase is sent only
 * for internal wallet orders (`wallet-…`, no Shopify order) — any order that exists in
 * Shopify is reported by Shopify's Meta app, so the site never sends Purchase for it.
 *
 * No-op on the server, when the pixel isn't configured, or when it's blocked.
 */
type FbqWindow = Window & { fbq?: (...args: unknown[]) => void };

export function trackMetaEvent(
  event: 'AddToCart' | 'InitiateCheckout' | 'AddPaymentInfo' | 'Purchase',
  params: {
    value?: number;
    currency?: string;
    content_ids?: string[];
    content_name?: string;
    num_items?: number;
    contents?: { id: string; quantity: number; item_price?: number }[];
  },
  eventId?: string,
) {
  if (typeof window === 'undefined') return;
  const fbq = (window as FbqWindow).fbq;
  if (typeof fbq !== 'function') return;
  // Meta's catalog (synced by Shopify's Meta app) keys products by the numeric variant id,
  // not the Storefront API gid — send the bare number so events match catalog items.
  const bare = (id: string) => id.replace(/^gid:\/\/shopify\/ProductVariant\//, '');
  const content_ids = params.content_ids?.map(bare);
  const contents = params.contents?.map((c) => ({ ...c, id: bare(c.id) }));
  try {
    fbq(
      'track',
      event,
      { content_type: 'product', ...params, content_ids, ...(contents ? { contents } : {}) },
      ...(eventId ? [{ eventID: eventId }] : []),
    );
  } catch {
    /* never let analytics break checkout */
  }
}
